import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../../promociones/styles/promociones.css";
import "../styles/calendarioFeriadosModal.css";
import {
  listarCalendarioNoLaborable,
  sugerirFeriados,
  crearCalendarioNoLaborable,
  actualizarCalendarioNoLaborable,
  eliminarCalendarioNoLaborable,
} from "../../../../services/calendarioNoLaborableService";
import { showToast } from "../../../../utils/showToast";

const WEEK_LABELS = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];
const MONTH_LABELS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const TIPO_OPTIONS = [
  { value: "nacional", label: "Nacional" },
  { value: "local", label: "Local" },
  { value: "cierre", label: "Cierre" },
];
const TIPO_COLOR = { nacional: "#e74c3c", local: "#f39c12", cierre: "#7f8c8d" };

const pad2 = (value) => String(value).padStart(2, "0");
const toDateKey = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const parseInputDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map((v) => Number(v));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};
const toDisplayDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = String(value).split("-").map((v) => Number(v));
  if (!year || !month || !day) return value;
  return `${pad2(day)}/${pad2(month)}/${year}`;
};

const buildCalendarWeeks = (year, month) => {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const start = new Date(first.getFullYear(), first.getMonth(), first.getDate() - startOffset);
  const weeks = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w += 1) {
    const week = [];
    for (let d = 0; d < 7; d += 1) {
      week.push({ date: new Date(cursor), inMonth: cursor.getMonth() === month });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
};

// Modal reutilizable para ver/editar feriados y días no laborables como
// calendario visual, en vez de la lista plana de Coordinación → Calendario.
// Pensado para abrirse desde cualquier pantalla que trabaje con fechas
// (Promoción, Cronograma, etc.) sin salir de esa pantalla — al cerrar avisa
// si hubo cambios (onClose(changed)) para que el que lo abrió refresque su
// propio calendario de días no laborables.
export default function CalendarioNoLaborableCalendarModal({ open, onClose, initialDate }) {
  const [activeMonth, setActiveMonth] = useState(() => parseInputDate(initialDate) || new Date());
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const changedRef = useRef(false);

  // Feriados sugeridos desde una API pública (revisión manual, nunca se
  // agregan solos): se buscan para el año del mes que se está viendo.
  const [showSugeridos, setShowSugeridos] = useState(false);
  const [sugeridosLoading, setSugeridosLoading] = useState(false);
  const [sugeridosError, setSugeridosError] = useState("");
  const [sugeridosLista, setSugeridosLista] = useState([]);
  const [sugeridosSeleccion, setSugeridosSeleccion] = useState(() => new Set());
  const [importando, setImportando] = useState(false);

  useEffect(() => {
    if (!open) return;
    changedRef.current = false;
    setForm(null);
    setShowSugeridos(false);
    setActiveMonth(parseInputDate(initialDate) || new Date());
  }, [open, initialDate]);

  const fetchMes = useCallback(() => {
    const weeks = buildCalendarWeeks(activeMonth.getFullYear(), activeMonth.getMonth());
    const days = weeks.flat().map((item) => item.date);
    const desde = toDateKey(days[0]);
    const hasta = toDateKey(days[days.length - 1]);
    setLoading(true);
    listarCalendarioNoLaborable({ desde, hasta, activo: 1 })
      .then((data) => setRegistros(Array.isArray(data) ? data : []))
      .catch(() => showToast({ type: "danger", message: "No se pudieron cargar los feriados." }))
      .finally(() => setLoading(false));
  }, [activeMonth]);

  useEffect(() => {
    if (open) fetchMes();
  }, [open, fetchMes]);

  const buscarSugeridos = useCallback(() => {
    const anio = activeMonth.getFullYear();
    setShowSugeridos(true);
    setSugeridosLoading(true);
    setSugeridosError("");
    sugerirFeriados(anio)
      .then((data) => {
        const lista = Array.isArray(data) ? data : [];
        setSugeridosLista(lista);
        // Preseleccionados los que no existen todavía; los que ya están no se
        // pueden volver a marcar (evita duplicados).
        setSugeridosSeleccion(new Set(lista.filter((f) => !f.ya_existe).map((f) => f.fecha)));
      })
      .catch((err) => {
        setSugeridosLista([]);
        setSugeridosError(err.message || "No se pudo consultar el servicio de feriados.");
      })
      .finally(() => setSugeridosLoading(false));
  }, [activeMonth]);

  const toggleSugerido = (fecha) => {
    setSugeridosSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(fecha)) next.delete(fecha);
      else next.add(fecha);
      return next;
    });
  };

  const handleImportarSugeridos = async () => {
    const aImportar = sugeridosLista.filter((f) => !f.ya_existe && sugeridosSeleccion.has(f.fecha));
    if (!aImportar.length) {
      setShowSugeridos(false);
      return;
    }
    setImportando(true);
    let fallos = 0;
    for (const f of aImportar) {
      try {
        await crearCalendarioNoLaborable({
          fecha: f.fecha,
          tipo: "nacional",
          descripcion: f.descripcion,
          centro_id: null,
          activo: true,
        });
      } catch {
        fallos += 1;
      }
    }
    setImportando(false);
    setShowSugeridos(false);
    changedRef.current = true;
    fetchMes();
    if (fallos) {
      showToast({ type: "warning", message: `Se agregaron ${aImportar.length - fallos} de ${aImportar.length} feriados. ${fallos} ya existían o fallaron.` });
    } else {
      showToast({ type: "success", message: `Se agregaron ${aImportar.length} feriado${aImportar.length === 1 ? "" : "s"}.` });
    }
  };

  const registroPorFecha = useMemo(() => {
    const map = {};
    registros.forEach((r) => { map[String(r.fecha).slice(0, 10)] = r; });
    return map;
  }, [registros]);

  const handleDayClick = (key) => {
    const existente = registroPorFecha[key];
    if (existente) {
      setForm({
        id: existente.id,
        fecha: key,
        tipo: existente.tipo,
        descripcion: existente.descripcion || "",
        centro_id: existente.centro_id ? String(existente.centro_id) : "global",
      });
    } else {
      setForm({ id: null, fecha: key, tipo: "nacional", descripcion: "", centro_id: "global" });
    }
  };

  const handleGuardar = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        fecha: form.fecha,
        tipo: form.tipo,
        descripcion: form.descripcion?.trim() || null,
        centro_id: form.centro_id !== "global" ? Number(form.centro_id) : null,
        activo: true,
      };
      if (form.id) await actualizarCalendarioNoLaborable(form.id, payload);
      else await crearCalendarioNoLaborable(payload);
      changedRef.current = true;
      showToast({ type: "success", message: form.id ? "Fecha actualizada." : "Fecha agregada al calendario." });
      setForm(null);
      fetchMes();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo guardar." });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async () => {
    if (!form?.id) return;
    setSaving(true);
    try {
      await eliminarCalendarioNoLaborable(form.id);
      changedRef.current = true;
      showToast({ type: "success", message: "Fecha quitada del calendario." });
      setForm(null);
      fetchMes();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo quitar." });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const weeks = buildCalendarWeeks(activeMonth.getFullYear(), activeMonth.getMonth());

  return (
    <div className="modal fade show d-block feriados-calendario-modal" style={{ backgroundColor: "rgba(15,23,42,0.5)", zIndex: 1060 }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <div>
              <h5 className="modal-title mb-0">
                <i className="bi bi-calendar-week me-2" />
                Feriados y días no laborables
              </h5>
              <p className="text-muted small mb-0 mt-1">
                Clic en un día para agregarlo, editarlo o quitarlo. Los cambios se reflejan de inmediato al armar promociones y cronogramas.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={buscarSugeridos}
                title={`Buscar feriados nacionales de ${activeMonth.getFullYear()} en un servicio público`}
              >
                <i className="bi bi-search me-1" />
                Buscar feriados {activeMonth.getFullYear()}
              </button>
              <button type="button" className="btn-close" onClick={() => onClose?.(changedRef.current)} />
            </div>
          </div>
          <div className="modal-body">
            <div className="promocion-calendario">
              <div className="promocion-calendario-header">
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => setActiveMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                >
                  <i className="bi bi-chevron-left" />
                </button>
                <strong>{MONTH_LABELS[activeMonth.getMonth()]} {activeMonth.getFullYear()}</strong>
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => setActiveMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
              <div className="promocion-calendario-weekdays">
                {WEEK_LABELS.map((label) => <span key={label}>{label}</span>)}
              </div>
              <div className={`promocion-calendario-grid ${loading ? "is-loading" : ""}`}>
                {weeks.flat().map(({ date, inMonth }) => {
                  const key = toDateKey(date);
                  const reg = registroPorFecha[key];
                  const isSelected = form?.fecha === key;
                  const style = reg ? { background: TIPO_COLOR[reg.tipo] || TIPO_COLOR.nacional, color: "#fff" } : undefined;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={[
                        "promocion-calendario-day",
                        inMonth ? "is-current" : "is-outside",
                        isSelected ? "is-start" : "",
                      ].filter(Boolean).join(" ")}
                      style={style}
                      title={reg?.descripcion || ""}
                      onClick={() => handleDayClick(key)}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
              <div className="promocion-calendario-legend">
                <span className="legend-item">
                  <span className="legend-dot" style={{ background: TIPO_COLOR.nacional }} />
                  Nacional
                </span>
                <span className="legend-item">
                  <span className="legend-dot" style={{ background: TIPO_COLOR.local }} />
                  Local
                </span>
                <span className="legend-item">
                  <span className="legend-dot" style={{ background: TIPO_COLOR.cierre }} />
                  Cierre
                </span>
              </div>
            </div>

            {form && (
              <div className="border rounded p-3 mt-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>{form.id ? "Editar" : "Agregar"} — {toDisplayDate(form.fecha)}</strong>
                  <button type="button" className="btn-close btn-sm" onClick={() => setForm(null)} />
                </div>
                <div className="row g-2 align-items-end">
                  <div className="col-12 col-md-3">
                    <label className="form-label small fw-semibold mb-1">Tipo</label>
                    <select
                      className="form-select form-select-sm"
                      value={form.tipo}
                      onChange={(e) => setForm((prev) => ({ ...prev, tipo: e.target.value }))}
                    >
                      {TIPO_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="col-12 col-md-5">
                    <label className="form-label small fw-semibold mb-1">Descripción</label>
                    <input
                      className="form-control form-control-sm"
                      value={form.descripcion}
                      onChange={(e) => setForm((prev) => ({ ...prev, descripcion: e.target.value }))}
                      placeholder="Ej: Día del trabajo"
                    />
                  </div>
                  <div className="col-12 col-md-4 d-flex justify-content-start justify-content-md-end gap-2">
                    {form.id ? (
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleEliminar} disabled={saving}>
                        Quitar feriado
                      </button>
                    ) : null}
                    <button type="button" className="btn btn-sm management-btn-primary" onClick={handleGuardar} disabled={saving}>
                      {saving ? "Guardando..." : (form.id ? "Guardar cambios" : "Agregar")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showSugeridos && (
              <div className="border rounded p-3 mt-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Feriados sugeridos {activeMonth.getFullYear()}</strong>
                  <button type="button" className="btn-close btn-sm" onClick={() => setShowSugeridos(false)} />
                </div>
                <p className="text-muted small mb-2">
                  Fuente: servicio público (Nager.Date). Revisa antes de agregar — no siempre aplica el traslado
                  de feriados a lunes ni incluye feriados locales de Ecuador.
                </p>
                {sugeridosLoading ? (
                  <div className="text-center py-3">
                    <span className="spinner-border spinner-border-sm" role="status" />
                  </div>
                ) : sugeridosError ? (
                  <div className="alert alert-danger py-2 small mb-0">{sugeridosError}</div>
                ) : sugeridosLista.length === 0 ? (
                  <p className="text-muted small mb-0">No se encontraron feriados para este año.</p>
                ) : (
                  <>
                    <div style={{ maxHeight: 220, overflowY: "auto" }} className="border rounded bg-white mb-2">
                      {sugeridosLista.map((f) => (
                        <label
                          key={f.fecha}
                          className={`d-flex align-items-center gap-2 px-2 py-1 small mb-0 ${f.ya_existe ? "text-muted" : ""}`}
                          style={{ cursor: f.ya_existe ? "default" : "pointer", borderBottom: "1px solid #eee" }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            checked={sugeridosSeleccion.has(f.fecha)}
                            disabled={f.ya_existe}
                            onChange={() => toggleSugerido(f.fecha)}
                          />
                          <span className="font-monospace">{toDisplayDate(f.fecha)}</span>
                          <span>{f.descripcion}</span>
                          {f.ya_existe ? <span className="badge bg-secondary ms-auto">Ya agregado</span> : null}
                        </label>
                      ))}
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowSugeridos(false)} disabled={importando}>
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm management-btn-primary"
                        onClick={handleImportarSugeridos}
                        disabled={importando || sugeridosSeleccion.size === 0}
                      >
                        {importando ? "Agregando..." : `Agregar ${sugeridosSeleccion.size} seleccionado${sugeridosSeleccion.size === 1 ? "" : "s"}`}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer border-0 pt-0">
            <button type="button" className="btn btn-outline-secondary" onClick={() => onClose?.(changedRef.current)}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
